import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import useApi from "hooks/useApi";
import { useEffect, useState } from "react";
import { IoReloadOutline, IoTodayOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import ReactLoading from "react-loading";

const TodaysSummary = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  const [data, setData] = useState();
  const { loading, fetchData } = useApi();

  const loadSummary = async () => {
    const endpoint = {
      method: "post",
      url: `/api/admin/report/todays-summary`,
    };
    const result = await fetchData(endpoint, false);
    if (result.success) {
      const data = result?.data;
      setData(data);
    }
  };

  useEffect(() => {
    if (openDialog) {
      loadSummary();
    }
  }, [openDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Tooltip title="Today's Summary">
        <IconButton
          color="inherit"
          onClick={() => setOpenDialog(true)}
          sx={{
            "& > svg": {
              color: (theme) => theme.palette.primary.main,
              fontSize: "21px",
            },
          }}
        >
          <IoTodayOutline />
        </IconButton>
      </Tooltip>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="todays-summary-dialog-title"
        TransitionProps={{
          timeout: 500,
        }}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            border: "2px solid #ffd1a2",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <DialogTitle
          id="todays-summary-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#fff7da",
            color: "#405189",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              "& > svg": {
                color: (theme) => theme.palette.primary.main,
                fontSize: "22px",
              },
            }}
          >
            <IoTodayOutline />
            <Typography variant="h5" fontWeight={600} m={0} p={0}>
              Today's Summary
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadSummary}
                disabled={loading}
                sx={{ color: "primary.main" }}
              >
                <IoReloadOutline />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ color: "white" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ padding: "24px" }}>
          {loading && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReactLoading
                  type="cylon"
                  color="#405189"
                  height={"85px"}
                  width={"100px"}
                />
              </Box>
            </Box>
          )}
          {!loading && !data && (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
              No summary data.
            </Box>
          )}
          {!loading && data && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {[
                { key: "leads", label: "Leads", metrics: data?.leads },
                // { key: "projects", label: "Projects", metrics: data?.projects },
                {
                  key: "revenue-records/invoices",
                  label: "Invoices",
                  metrics: data?.invoices,
                },
                {
                  key: "revenue-records/sales",
                  label: "Subscriptions",
                  metrics: data?.sales,
                },
              ].map((group) => (
                <Box
                  key={group.key}
                  sx={{
                    p: 2,
                    border: "1px solid #eee",
                    borderRadius: 2,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Box sx={{ fontWeight: 600, fontSize: "16px", mb: 1.5 }}>
                    {group.label}
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(1, 1fr)",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                      },
                      gap: 1.5,
                    }}
                  >
                    {Object.entries(group.metrics || {}).map(
                      ([metricKey, count]) => {
                        const filterKey = metricKey.replace(/_/g, "-");
                        const prettyTitle = metricKey
                          .split("_")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ");
                        return (
                          <Box
                            key={metricKey}
                            sx={{
                              p: 1.5,
                              border: "1px solid #ddd",
                              borderRadius: 1.5,
                              backgroundColor: "white",
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              position: "relative",
                            }}
                          >
                            <Box sx={{ fontSize: "13px", fontWeight: 500 }}>
                              {metricKey === "follow_up_date"
                                ? "Follow Up Remaining"
                                : prettyTitle}
                            </Box>
                            <Box
                              sx={{
                                fontSize: "20px",
                                fontWeight: 700,
                                color: "primary.main",
                              }}
                            >
                              {count}
                            </Box>
                            <Typography
                              component="span"
                              color="primary"
                              fontWeight={500}
                              sx={{
                                fontSize: "13px",
                                mt: 0.5,
                                textTransform: "none",
                                padding: "0 !important",
                                cursor: "pointer",
                                "&:hover": { textDecoration: "underline" },
                              }}
                              onClick={() => {
                                setOpenDialog(false);
                                navigate(
                                  `/${group.key}?filter=todays-${filterKey}`
                                );
                              }}
                            >
                              Show
                            </Typography>
                          </Box>
                        );
                      }
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="solid"
            color="primary"
            sx={{
              fontWeight: "bold",
              minWidth: "100px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default TodaysSummary;
